import { plansApi } from "@/lib/apimock";
import { getPlans } from "@/lib/plan/api";
import { Plan } from "@/types";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
export function useGetPlans() {
  return useSuspenseQuery({
    queryKey: ["plans"],
    queryFn: async () => {
      try {
        const response = await getPlans();
        return response;
      } catch (error) {
        console.error("Failed to fetch plans:", error);
        // Return a valid structure instead of throwing
        return { data: { items: [] } };
      }
    },
    select: (data) => {
      try {
        // Handle different possible response structures
        if (data?.data?.items) {
          return data.data.items as Plan[];
        }
        // If the response is already an array
        if (Array.isArray(data?.data)) {
          return data.data as Plan[];
        }
        // If data is directly the array
        if (Array.isArray(data)) {
          return data as Plan[];
        }
        // Fallback to empty array if structure is unexpected
        console.warn("Unexpected plans API response structure:", data);
        return [] as Plan[];
      } catch (error) {
        console.error("Error in plans select function:", error);
        return [] as Plan[];
      }
    },
  });
}

export function usePlans(enabled = true) {
  const queryClient = useQueryClient();

  // Get all plans
  const {
    data: plans = [],
    isLoading: isLoadingPlans,
    error: plansError,
  } = useQuery({
    queryKey: ["plans"],
    queryFn: plansApi.getPlans,
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled,
  });

  // Subscribe to plan mutation
  const subscribeToPlanMutation = useMutation({
    mutationFn: plansApi.subscribeToPlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      queryClient.invalidateQueries({ queryKey: ["plans"] });
    },
  });

  const subscribeToPlan = async (planId: string) => {
    try {
      await subscribeToPlanMutation.mutateAsync(planId);
      return { success: true };
    } catch (error) {
      return { success: false, error: "Failed to subscribe to plan" };
    }
  };

  return {
    plans,
    isLoadingPlans,
    plansError,
    subscribeToPlan,
    isSubscribing: subscribeToPlanMutation.isPending,
  };
}
